/**
 * Agent chat thread persistence.
 *
 * The thread outlives the panel, the route and a same-tab reload, so the rules
 * for bringing it back are worth keeping in one testable place rather than
 * inline in the component.
 *
 * Storage is sessionStorage, deliberately: the transcript can contain PHI, so
 * it must die with the browser tab and must never reach localStorage, where it
 * would survive logout.
 */

import type { ChatLayout, ChatMessage } from './types';

// The ":v1" suffix lets us invalidate the cache if the ChatMessage shape
// changes.
export const STORAGE_KEY = 'phanova:agent:chat:v1';
export const EXPANDED_KEY = 'phanova:agent:chat:expanded:v1';

// PHA-1071: a thread stays open until the clinician ends it, so it expires on
// idle time rather than on the calendar day. The previous rule dropped any
// message whose timestamp was not `toDateString() === today`, which silently
// emptied a conversation that crossed midnight — while still sending the full
// transcript to the model as history, so the agent answered as though turns the
// clinician could no longer see had happened.
//
// Twelve hours keeps a morning huddle alive through an afternoon follow-up
// without resurrecting last week's conversation.
export const THREAD_IDLE_MS = 12 * 60 * 60 * 1000;

// Pending approvals live server-side in a module-level Map with a 10-minute TTL
// and are lost outright when the BFF restarts (chiefOfStaffAgent.js). A
// restored thread can therefore carry an approval card whose id no longer
// resolves, where pressing Approve fails with a confusing error. Strip the card
// on hydrate and say what happened instead.
export const APPROVAL_TTL_MS = 10 * 60 * 1000;

export const EXPIRED_APPROVAL_NOTE =
  '_This action expired before it was approved. Ask again if you still want it._';

function ageMs(timestamp: string | undefined, now: number): number {
  if (!timestamp) return Infinity;
  const t = new Date(timestamp).getTime();
  if (Number.isNaN(t)) return Infinity;
  return now - t;
}

/**
 * Rehydrate a stored transcript. Pure apart from the storage read so the rules
 * above can be tested directly.
 */
export function hydrateThread(raw: string | null, now: number = Date.now()): ChatMessage[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const messages = (parsed as ChatMessage[]).filter(
    (m) => m && typeof m === 'object' && typeof m.id === 'string'
  );
  if (!messages.length) return [];

  // Anchored on the LAST message, so an active conversation never expires out
  // from under the user. Drop the whole thread rather than showing a fragment.
  const last = messages[messages.length - 1];
  if (last?.timestamp && ageMs(last.timestamp, now) > THREAD_IDLE_MS) return [];

  return messages.map((m) => {
    const next: ChatMessage = { ...m, isStreaming: false };
    if (next.pendingApproval && ageMs(m.timestamp, now) > APPROVAL_TTL_MS) {
      delete next.pendingApproval;
      next.content = `${next.content || ''}\n\n${EXPIRED_APPROVAL_NOTE}`.trim();
    }
    return next;
  });
}

export function loadPersistedMessages(): ChatMessage[] {
  if (typeof window === 'undefined') return [];
  try {
    return hydrateThread(window.sessionStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return [];
  }
}

export function persistMessages(messages: ChatMessage[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  } catch (e) {
    // Quota exceeded or storage disabled — not worth surfacing to the user.
  }
}

export function clearPersistedMessages(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

// PHA-1071: stable identity for one chat thread, sent with every turn so the
// backend can upsert into a single durable conversation. Created lazily on the
// first message and cleared by "New chat", so a thread's server-side identity
// matches exactly what the clinician sees in the panel.
export const CONVERSATION_KEY = 'phanova:agent:chat:conversation:v1';

export function getOrCreateConversationKey(): string {
  const fallback = () =>
    `conv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  if (typeof window === 'undefined') return fallback();
  try {
    const existing = window.sessionStorage.getItem(CONVERSATION_KEY);
    if (existing) return existing;
    const created =
      typeof window.crypto?.randomUUID === 'function'
        ? `conv_${window.crypto.randomUUID()}`
        : fallback();
    window.sessionStorage.setItem(CONVERSATION_KEY, created);
    return created;
  } catch (e) {
    // Storage disabled: the turn still works, it just won't be grouped with
    // the rest of the thread server-side.
    return fallback();
  }
}

// PHA-1155: the current thread's key without creating one (History highlights
// the open thread; an empty panel has none yet).
export function peekConversationKey(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(CONVERSATION_KEY);
  } catch (e) {
    return null;
  }
}

// PHA-1155: continue a thread reopened from History under its own key, so new
// turns append to the same durable conversation.
export function setConversationKey(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(CONVERSATION_KEY, key);
  } catch (e) {
    // ignore
  }
}

export type StoredTurn = {
  turnIndex: number;
  role: string;
  content: string;
  agent?: string;
  createdAt?: string | null;
};

// PHA-1155: rebuild a transcript from the durable store.
//
// The store keys turns by position in the client's transcript, and UI-only
// system notices ("Now viewing …") take a position without ever being stored.
// Reloading only the stored turns would shift every later position down, and
// the next turn would overwrite an existing row. Gaps are therefore filled
// with hidden placeholders so positions line up with stored turn indexes.
export function threadFromStored(turns: StoredTurn[], idPrefix = 'h'): ChatMessage[] {
  const sorted = [...turns]
    .filter((t) => t && (t.role === 'user' || t.role === 'assistant' || t.role === 'system'))
    .sort((a, b) => a.turnIndex - b.turnIndex);
  const out: ChatMessage[] = [];
  for (const t of sorted) {
    while (out.length < t.turnIndex) {
      out.push({
        id: `${idPrefix}-gap-${out.length}`,
        role: 'system',
        content: '(earlier notice)',
        hidden: true,
      });
    }
    if (out.length > t.turnIndex) continue; // duplicate index -- keep the first
    out.push({
      id: `${idPrefix}-${t.turnIndex}`,
      role: t.role as ChatMessage['role'],
      content: t.content || '',
      timestamp: t.createdAt || undefined,
      agent: (t.agent as ChatMessage['agent']) || undefined,
      streamComplete: true,
    });
  }
  return out;
}

// PHA-1155: layout preference (panel / side sheet / full screen) and the side
// sheet's width. Not PHI, so it lives in localStorage and follows the user
// across tabs.
export const LAYOUT_KEY = 'phanova:agent:layout:v1';
export type LayoutPrefs = { mode: ChatLayout; sideWidth: number };
const DEFAULT_LAYOUT: LayoutPrefs = { mode: 'panel', sideWidth: 560 };

export function loadLayoutPrefs(): LayoutPrefs {
  if (typeof window === 'undefined') return DEFAULT_LAYOUT;
  try {
    const raw = JSON.parse(window.localStorage.getItem(LAYOUT_KEY) || 'null');
    const mode: ChatLayout = raw && ['panel', 'side', 'full'].includes(raw.mode) ? raw.mode : 'panel';
    const sideWidth = Number.isFinite(raw?.sideWidth) ? Math.min(Math.max(raw.sideWidth, 400), 1100) : 560;
    return { mode, sideWidth };
  } catch (e) {
    return DEFAULT_LAYOUT;
  }
}

export function persistLayoutPrefs(prefs: LayoutPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAYOUT_KEY, JSON.stringify(prefs));
  } catch (e) {
    // ignore
  }
}

export function clearConversationKey(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(CONVERSATION_KEY);
  } catch (e) {
    // ignore
  }
}

export function loadPersistedExpanded(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(EXPANDED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function persistExpanded(expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(EXPANDED_KEY, expanded ? '1' : '0');
  } catch (e) {
    // Storage disabled — the panel still works, it just won't restore.
  }
}
